/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Row, Space, Tooltip, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS, SWITCH_QUERY_HANDLE_IDS } from '../../../../constants/flowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Text } = Typography;

const SwitchQueryNode = ({ id, type, data, dragging, selected }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { graphDirection } = useContext(GraphContext);
  const { currentBotLanguage } = useBotLanguage();

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>

        <div className="content">
          <Space size={6} className="node-header">
            {nodeProps.icon}
            {nodeProps.label}
          </Space>

          {
            data.switchQuery?.[currentBotLanguage]? (
              <Tooltip
                placement="leftBottom"
                title={data.switchQuery[currentBotLanguage]}
                arrowPointAtCenter
              >
                <Text className="text-color-default" ellipsis>
                  {data.switchQuery[currentBotLanguage]}
                </Text>
              </Tooltip>
            ) : (
              <Text className="text-secondary">
                {t('Click to edit content')}
              </Text>
            )
          }
        </div>

        <Row justify="space-evenly" align="stretch">
          {/* If layout is horizontal, col should have span 24, otherwise no span */}
          <Col {...(graphDirection === GRAPH_LAYOUTS.VERTICAL ? {} : { span: 24 })}>
            <LimitedConnectionsHandle
              type="source"
              id={SWITCH_QUERY_HANDLE_IDS.TRUE}
              source={id}
              className="border-color-success"
              style={graphDirection === GRAPH_LAYOUTS.VERTICAL ? {} : { top: '-42px' }}
              position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}
              allowedOutgoingEdgesCount={1}
            />
          </Col>

          <Col {...(graphDirection === GRAPH_LAYOUTS.VERTICAL ? {} : { span: 24 })}>
            <LimitedConnectionsHandle
              type="source"
              id={SWITCH_QUERY_HANDLE_IDS.FALSE}
              source={id}
              className="border-color-error"
              style={graphDirection === GRAPH_LAYOUTS.VERTICAL ? {} : { top: '-22px' }}
              position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}
              allowedOutgoingEdgesCount={1}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
SwitchQueryNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(SwitchQueryNode);
